import React, { useCallback, useEffect, useRef, useState } from 'react';

import Api from '../../Api';
import { withAuth } from '../../hoc/Auth';
import { Column, Container, Wrapper } from '../../hoc/Layout';

import classes from './styles.module.css';

const TAB_HELD = 'held';
const TAB_HISTORY = 'history';
const TAB_CARDS = 'cards';

const STATUS_LABELS = {
    held: 'Ждёт решения',
    finalized: 'Проведена без провайдера',
    released: 'Передана провайдеру',
    declined: 'Отклонена',
};

function formatDate(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('ru-RU');
}

function errorText(error) {
    const data = error && error.response && error.response.data;
    if (data && data.message) return data.message;
    return 'Не удалось выполнить действие. Попробуйте ещё раз.';
}

/**
 * Выплаты, остановленные по списку карт.
 *
 * Выплату на карту из списка система не отправляет провайдеру: она висит
 * «в обработке», пока администратор не решит здесь — провести без провайдера,
 * передать провайдеру или отклонить. Больше нигде с такой выплатой ничего
 * сделать нельзя.
 */
const PayoutHoldsPage = withAuth(function ({ token }) {
    const [tab, setTab] = useState(TAB_HELD);
    const [holds, setHolds] = useState([]);
    const [summary, setSummary] = useState(null);
    const [cards, setCards] = useState([]);
    const [showRemoved, setShowRemoved] = useState(false);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [message, setMessage] = useState(null);
    const [cardsText, setCardsText] = useState('');
    const [cardsComment, setCardsComment] = useState('');
    const requestedTab = useRef(tab);

    const loadHolds = useCallback(() => {
        requestedTab.current = tab;
        setLoading(true);
        new Api({ token })
            .getPayoutHolds(tab === TAB_HELD ? 'held' : 'resolved')
            .then((r) => {
                // ответ на запрос прошлой вкладки, пришедший позже, не показываем
                if (requestedTab.current !== tab) return;
                setHolds(Array.isArray(r.items) ? r.items : []);
                setSummary(r.summary || null);
            })
            .catch((e) => setMessage({ type: 'error', text: errorText(e) }))
            .finally(() => setLoading(false));
    }, [token, tab]);

    const loadCards = useCallback(() => {
        requestedTab.current = TAB_CARDS;
        setLoading(true);
        new Api({ token })
            .getPayoutHoldCards(showRemoved)
            .then((r) => {
                if (requestedTab.current !== TAB_CARDS) return;
                setCards(Array.isArray(r.items) ? r.items : []);
            })
            .catch((e) => setMessage({ type: 'error', text: errorText(e) }))
            .finally(() => setLoading(false));
    }, [token, showRemoved]);

    useEffect(() => {
        if (tab === TAB_CARDS) {
            loadCards();
        } else {
            loadHolds();
        }
    }, [tab, loadHolds, loadCards]);

    function target(hold) {
        return hold.transactionId ? `транзакция #${hold.transactionId}` : `CRM-заявка #${hold.payoutRequestId}`;
    }

    function decide(hold, action) {
        const what = `${target(hold)}, ${hold.amount} ${hold.currency || ''}, карта ${hold.cardMasked}`;
        const texts = {
            finalize: `Провести выплату без отправки провайдеру?\n${what}\n\nС баланса мерчанта будет списана сумма с комиссией, мерчант получит уведомление «успех».`,
            release: `Передать выплату провайдеру?\n${what}\n\nВыплата уйдёт в банк обычным путём.`,
            decline: `Отклонить выплату?\n${what}\n\nДеньги мерчанту не списываются, он получит уведомление об отказе.`,
        };
        if (!window.confirm(texts[action])) return;

        const comment = window.prompt(
            action === 'decline' ? 'Причина отказа (её увидит мерчант):' : 'Комментарий (для журнала):',
            action === 'decline' ? 'Выплата на эту карту недоступна' : ''
        );
        if (comment === null) return;

        const api = new Api({ token });
        const call = {
            finalize: () => api.finalizePayoutHold(hold.id, comment),
            release: () => api.releasePayoutHold(hold.id, comment),
            decline: () => api.declinePayoutHold(hold.id, comment),
        }[action];

        setBusyId(hold.id);
        setMessage(null);
        call()
            .then(() => {
                setMessage({ type: 'success', text: `Готово: ${target(hold)} — ${STATUS_LABELS[{ finalize: 'finalized', release: 'released', decline: 'declined' }[action]].toLowerCase()}.` });
                loadHolds();
            })
            .catch((e) => {
                setMessage({ type: 'error', text: errorText(e) });
                loadHolds();
            })
            .finally(() => setBusyId(null));
    }

    function addCards() {
        if (cardsText.trim() === '') {
            setMessage({ type: 'error', text: 'Вставьте номера карт, по одному на строку.' });
            return;
        }
        setBusyId('cards');
        setMessage(null);
        new Api({ token })
            .addPayoutHoldCards(cardsText, cardsComment)
            .then((r) => {
                const parts = [];
                if (r.added && r.added.length) parts.push(`добавлено: ${r.added.join(', ')}`);
                if (r.reactivated && r.reactivated.length) parts.push(`возвращено в список: ${r.reactivated.join(', ')}`);
                if (r.existing && r.existing.length) parts.push(`уже в списке: ${r.existing.join(', ')}`);
                if (r.invalid && r.invalid.length) parts.push(`ошибки: ${r.invalid.map((i) => `строка ${i.line} — ${i.reason}`).join('; ')}`);
                if (r.heldRequests) parts.push(`остановлено открытых CRM-заявок: ${r.heldRequests}`);
                setMessage({ type: r.invalid && r.invalid.length ? 'error' : 'success', text: parts.join('. ') || 'Ничего не изменилось.' });
                setCardsText('');
                loadCards();
            })
            .catch((e) => setMessage({ type: 'error', text: errorText(e) }))
            .finally(() => setBusyId(null));
    }

    function removeCard(card) {
        if (!window.confirm(`Снять карту ${card.cardMasked} из списка?\nНовые выплаты на неё пойдут обычным путём. Уже остановленные продолжат ждать решения.`)) return;
        setBusyId(`card-${card.id}`);
        new Api({ token })
            .removePayoutHoldCard(card.id)
            .then(() => {
                setMessage({ type: 'success', text: `Карта ${card.cardMasked} снята из списка.` });
                loadCards();
            })
            .catch((e) => setMessage({ type: 'error', text: errorText(e) }))
            .finally(() => setBusyId(null));
    }

    function renderHolds() {
        if (loading) return <div className={classes.Empty}>Загрузка...</div>;
        if (holds.length === 0) {
            return <div className={classes.Empty}>{tab === TAB_HELD ? 'Остановленных выплат нет.' : 'Решений пока нет.'}</div>;
        }

        return (
            <div className={classes.TableScroll}>
                <table className={classes.Table}>
                    <thead>
                        <tr>
                            <th>Остановлена</th>
                            <th>Мерчант</th>
                            <th>Операция</th>
                            <th>Сумма</th>
                            <th>Карта</th>
                            <th>{tab === TAB_HELD ? 'Решение' : 'Итог'}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {holds.map((hold) => (
                            <tr key={hold.id}>
                                <td>{formatDate(hold.heldAt)}</td>
                                <td>{hold.merchantName || '—'}{hold.merchantId ? ` (#${hold.merchantId})` : ''}</td>
                                <td>{target(hold)}</td>
                                <td className={classes.Nowrap}>{hold.amount} {hold.currency || ''}</td>
                                <td className={classes.Nowrap}>{hold.cardMasked}</td>
                                <td>
                                    {hold.status === 'held' ? (
                                        <div className={classes.Actions}>
                                            <button type="button" className={classes.BtnPrimary} disabled={busyId !== null} onClick={() => decide(hold, 'finalize')}>
                                                Провести без провайдера
                                            </button>
                                            <button
                                                type="button"
                                                className={classes.BtnSecondary}
                                                disabled={busyId !== null || !hold.canRelease}
                                                title={hold.canRelease ? '' : 'Номер карты не сохранён — можно только провести или отклонить'}
                                                onClick={() => decide(hold, 'release')}
                                            >
                                                Передать провайдеру
                                            </button>
                                            <button type="button" className={classes.BtnDanger} disabled={busyId !== null} onClick={() => decide(hold, 'decline')}>
                                                Отклонить
                                            </button>
                                        </div>
                                    ) : (
                                        <div>
                                            <div>{STATUS_LABELS[hold.status] || hold.status}</div>
                                            <div className={classes.Muted}>{formatDate(hold.resolvedAt)}</div>
                                            {hold.resolutionComment ? <div className={classes.Muted}>{hold.resolutionComment}</div> : null}
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    function renderCards() {
        return (
            <div>
                <div className={classes.Form}>
                    <label className={classes.Label} htmlFor="hold-cards">Номера карт — по одному на строку (пробелы и дефисы допустимы)</label>
                    <textarea
                        id="hold-cards"
                        className={classes.Textarea}
                        rows={5}
                        value={cardsText}
                        onChange={(e) => setCardsText(e.target.value)}
                        autoComplete="off"
                    />
                    <label className={classes.Label} htmlFor="hold-comment">Комментарий</label>
                    <input
                        id="hold-comment"
                        className={classes.Input}
                        value={cardsComment}
                        onChange={(e) => setCardsComment(e.target.value)}
                        placeholder="Например: долг по выплатам 26.08"
                    />
                    <button type="button" className={classes.BtnPrimary} disabled={busyId !== null} onClick={addCards}>
                        Добавить в список
                    </button>
                </div>

                <label className={classes.Checkbox}>
                    <input type="checkbox" checked={showRemoved} onChange={() => setShowRemoved(!showRemoved)} /> показать снятые
                </label>

                {loading ? (
                    <div className={classes.Empty}>Загрузка...</div>
                ) : cards.length === 0 ? (
                    <div className={classes.Empty}>Список пуст.</div>
                ) : (
                    <div className={classes.TableScroll}>
                        <table className={classes.Table}>
                            <thead>
                                <tr>
                                    <th>Карта</th>
                                    <th>Комментарий</th>
                                    <th>Добавлена</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {cards.map((card) => (
                                    <tr key={card.id} className={card.isActive ? '' : classes.Removed}>
                                        <td className={classes.Nowrap}>{card.cardMasked}</td>
                                        <td>{card.comment || '—'}</td>
                                        <td>{formatDate(card.createdAt)}</td>
                                        <td>
                                            {card.isActive ? (
                                                <button type="button" className={classes.BtnDanger} disabled={busyId !== null} onClick={() => removeCard(card)}>
                                                    Снять
                                                </button>
                                            ) : (
                                                <span className={classes.Muted}>снята {formatDate(card.removedAt)}</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    }

    return (
        <Wrapper>
            <Container>
                <Column width="1100">
                    <div className={classes.Tabs}>
                        {[
                            [TAB_HELD, 'Ожидают решения'],
                            [TAB_HISTORY, 'История решений'],
                            [TAB_CARDS, 'Список карт'],
                        ].map(([key, title]) => (
                            <div
                                key={key}
                                className={`${classes.Tab} ${tab === key ? classes.TabActive : ''}`}
                                onClick={() => {
                                    setMessage(null);
                                    setTab(key);
                                }}
                            >
                                {title}
                            </div>
                        ))}
                    </div>

                    <div className={classes.Card}>
                        <div className={classes.Header}>
                            <h5 className={classes.Title}>
                                Остановленные выплаты
                                {summary && summary.count > 0 ? (
                                    <span className={classes.StatsInfo}>
                                        (ждут решения: {summary.count}
                                        {Array.isArray(summary.byCurrency) && summary.byCurrency.length > 0
                                            ? ` на ${summary.byCurrency.map((c) => `${(c.amount / 100).toFixed(2)} ${c.currency || ''}`).join(' + ')}`
                                            : ''}
                                        )
                                    </span>
                                ) : null}
                            </h5>
                        </div>
                        <div className={classes.Body}>
                            <p className={classes.Hint}>
                                Выплата на карту из списка не отправляется провайдеру и висит «в обработке», пока здесь не принято решение.
                                Больше нигде изменить её нельзя.
                            </p>
                            {message ? (
                                <div className={message.type === 'error' ? classes.MessageError : classes.MessageSuccess}>{message.text}</div>
                            ) : null}
                            {tab === TAB_CARDS ? renderCards() : renderHolds()}
                        </div>
                    </div>
                </Column>
            </Container>
        </Wrapper>
    );
});

export default PayoutHoldsPage;



// WEBPACK FOOTER //
// ./src/PagesOfAdministrator/PayoutHolds/index.js